import type {
  NavigationGuardReturn,
  NavigationGuardWithThis,
  RouteLocationNormalized,
  RouteRecordRaw,
} from 'vue-router'
import { useAppToast } from '@/composables/app-toast'
import type { AxiosError } from 'axios'
import { HttpStatusCode, isAxiosError } from 'axios'
import {
  CarrierAuctionStatusEnum,
  ConsentTypesEnum,
} from '@apiTypes'
import i18n from '@/plugins/i18n'
import { useAuctionsStore } from '@/stores/auction'
import { useAuthStore } from '@/stores/auth'
import { useCarriersStore } from '@store/carrier'
import { useAddressesStore } from '@/stores/address'
import {
  clearAuthData,
  isUserLoggedIn,
  refreshTokenHelper,
  switchWorkspaceHelper,
} from '@/auth/utils'
import { publicPages } from '@/router/public-pages'
import { useTendersStore } from '@/stores/tenders'
import { useCarrierTendersStore } from '@/stores/carrier-tenders'
import { useTransportsStore } from '@store/transport'
import { usePrivacyStore } from '@store/privacy'
import api from '@api'
import { PermissionObject, PermissionType } from '@/types/permissions'
import { userHasPermissions } from '@/utils/permissions.utils'
import * as Sentry from '@sentry/vue'
import { carrierPortalPages } from '@/router/carrier-portal-pages'
import { useCarrierPortalStore } from '@store/carrier-portal'
import { CARRIER_PORTAL_TOKEN } from '@/utils/const'
import { isCarrierBlockedError } from '@/composables/carrier-blocked'
import { useCarrierContractsByEmailStore } from '@/stores/carrier-contracts-by-email'
import { isContractAccessEnabledForWorkspace, isTenderEnabledForWorkspace } from '@/composables/module-quotas'
import { isTenderEditableDisplayStatus, tenderDisplayStatusLabel } from '@/utils/tender-display-status'
import { getTenderModifyPermissionType } from '@/utils/tender-permissions'
import { getAuctionModifyPermissionType } from '@/utils/auction-permissions'

type RouteGuard = NavigationGuardWithThis<undefined>
type RouteChangeGuard = (
  to: RouteLocationNormalized,
) => Promise<NavigationGuardReturn>

function makeQuotaGuard(isEnabled: () => boolean): () => NavigationGuardReturn {
  return () => {
    if (!isEnabled()) {
      useAppToast().warning(i18n.global.t('This module is not available for your workspace.'))

      return { name: 'request-list' }
    }

    return true
  }
}

function withQuotaGuard(
  isEnabled: () => boolean,
  existingGuard: (to: RouteLocationNormalized) => Promise<NavigationGuardReturn>,
): (to: RouteLocationNormalized) => Promise<NavigationGuardReturn> {
  const quotaGuard = makeQuotaGuard(isEnabled)

  return async (to: RouteLocationNormalized) => {
    const quotaResult = quotaGuard()
    if (quotaResult !== true) return quotaResult

    return existingGuard(to)
  }
}

const tenderQuotaGuard = makeQuotaGuard(isTenderEnabledForWorkspace)

const withTenderQuotaGuard = (
  existingGuard: (to: RouteLocationNormalized) => Promise<NavigationGuardReturn>,
) => withQuotaGuard(isTenderEnabledForWorkspace, existingGuard)

const contractAccessQuotaGuard = makeQuotaGuard(isContractAccessEnabledForWorkspace)

const withContractAccessQuotaGuard = (
  existingGuard: (to: RouteLocationNormalized) => Promise<NavigationGuardReturn>,
) => withQuotaGuard(isContractAccessEnabledForWorkspace, existingGuard)

export const routeGuardMap: Record<string, RouteGuard> = {
  'request-create': auctionCreateGuard,
  'request-copy-id': auctionCreateGuard,
  'request-edit-id': auctionEditGuard,
  'request-view-id': auctionViewChangedGuard,
  'directory-address-id': addressViewGuard,
  'transport-view-id': transportViewChangedGuard,
  'transport-edit-id': transportEditGuard,
  'contracts-tenders-edit-id': withTenderQuotaGuard(tenderEditGuard),
  'contracts-tenders-view-id': withTenderQuotaGuard(tenderViewGuard),
  'contracts-tenders-create': withTenderQuotaGuard(tenderCreateGuard),
  'contracts-tenders-copy-id': withTenderQuotaGuard(tenderCreateGuard),
  'contracts-list': contractAccessQuotaGuard,
  'contracts-contract-view-id': withContractAccessQuotaGuard(contractViewGuard),
  'tenders-list': tenderQuotaGuard,
  'carrier-chash-auction-hash': carrierAuctionGuard,
  'carrier-tender-hash': carrierTenderGuard,
  'carrier-contract-hash': carrierContractGuard,
  'privacy-preferences-hash': carrierConsentsGuard,
  'login': loggedUserRedirectGuard,
  'register': loggedUserRedirectGuard,
  'carrier-login': loggedCarrierRedirectGuard,
  'carrier-blocked': carrierBlockedPageGuard,
}

export const routeChangedGuardMap: Record<string, RouteChangeGuard> = {
  'request-view-id': auctionViewChangedGuard,
  'directory-address-id': addressViewChangedGuard,
  'transport-view-id': transportViewChangedGuard,
  'contracts-tenders-edit-id': withTenderQuotaGuard(tenderEditGuard),
  'contracts-tenders-view-id': withTenderQuotaGuard(tenderViewGuard),
}

export function extendRouteWithGuard(route: RouteRecordRaw): RouteRecordRaw {
  const routeName = route.children?.[0]?.name?.toString() || ''
  const guards: RouteGuard[] = []

  if (!publicPages.includes(routeName)) guards.push(dataGuard)

  if (carrierPortalPages.includes(routeName)) {
    guards.push(carrierPortalGuard)
  }

  if (routeGuardMap[routeName]) guards.push(routeGuardMap[routeName])

  return { ...route, beforeEnter: guards }
}

function paramsEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true
  if (Array.isArray(a) && Array.isArray(b))
    return a.length === b.length && a.every((v, i) => v === b[i])

  return false
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value)
}

export async function checkRouteChangedGuard(to: RouteLocationNormalized, from: RouteLocationNormalized): Promise<NavigationGuardReturn> {
  if (to.name !== from.name) return true

  if (paramsEqual(to.params.id, from.params.id)) return true

  const routeName = typeof to.name === 'string' ? to.name : ''
  const guard = routeChangedGuardMap[routeName]
  if (guard) return await guard(to)

  return true
}

/**
 * Preserve route meta on same-route navigations when only query or hash changed.
 * Route guards (e.g. tenderEditGuard) run only on beforeEnter, so query-only
 * updates get a new `to` without meta (e.g. tenderOverview), which can hide
 * the page content. Copy meta from `from` so state is preserved.
 */
export function preserveMetaOnSameRouteQueryChange(
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
): void {
  if (to.name !== from.name) return

  const sameParams = JSON.stringify(to.params) === JSON.stringify(from.params)

  if (!sameParams) return
  if (Object.keys(from.meta).length === 0) return

  Object.assign(to.meta, from.meta)
}

async function dataGuard(): Promise<NavigationGuardReturn> {
  if (isUserLoggedIn()) {
    try {
      await fetchData()

      return true
    }
    catch (e: unknown) {
      if (isAxiosError(e))
        return await checkGuardDataError(e, undefined, 'login')

      return { name: 'error' }
    }
  }

  return { name: 'login' }
}

async function auctionViewChangedGuard(to: RouteLocationNormalized): Promise<NavigationGuardReturn> {
  const id = +to.params.id
  const auctionsStore = useAuctionsStore()
  const toast = useAppToast()

  try {
    to.meta.auction = (await auctionsStore.fetchAuction(id)).data

    return true
  }
  catch {
    toast.error(
      i18n.global.t(
        'Request does not exist in this workspace, or you do not have access to it',
      ),
    )

    return { name: 'request-list' }
  }
}

async function addressViewGuard(to: RouteLocationNormalized): Promise<NavigationGuardReturn> {
  return addressViewChangedGuard(to)
}

async function addressViewChangedGuard(to: RouteLocationNormalized): Promise<NavigationGuardReturn> {
  const id = +to.params.id
  const addressesStore = useAddressesStore()
  const toast = useAppToast()

  try {
    to.meta.address = await addressesStore.fetchAddress(id).then(r => r.data)

    return true
  }
  catch {
    toast.error(i18n.global.t('Address does not exist in this workspace, or you do not have access to it'))

    return { name: 'directory-list', query: { tab: 'addresses' } }
  }
}

function auctionCreateGuard(): NavigationGuardReturn {
  return checkPermission(PermissionObject.Request, PermissionType.Create, 'request-list')
}

async function auctionEditGuard(to: RouteLocationNormalized): Promise<NavigationGuardReturn> {
  const id = +to.params.id
  if (!Number.isFinite(id) || id <= 0) {
    return { name: 'request-list' }
  }

  const auctionsStore = useAuctionsStore()
  const toast = useAppToast()

  try {
    const auction = (await auctionsStore.fetchAuction(id)).data
    const permType = getAuctionModifyPermissionType(auction.status)
    const permResult = checkPermission(PermissionObject.Request, permType, 'request-list')
    if (permResult !== true) return permResult

    to.meta.auction = auction

    return true
  }
  catch {
    toast.error(i18n.global.t('Request does not exist in this workspace, or you do not have access to it'))

    return { name: 'request-list' }
  }
}

async function transportViewChangedGuard(to: RouteLocationNormalized): Promise<NavigationGuardReturn> {
  const id = +to.params.id
  const transportsStore = useTransportsStore()
  const toast = useAppToast()

  try {
    to.meta.transport = (await transportsStore.fetchTransport(id)).data

    return true
  }
  catch {
    toast.error(
      i18n.global.t(
        'Transport does not exist in this workspace, or you do not have access to it',
      ),
    )

    return { name: 'transport-list' }
  }
}

function transportEditGuard(): NavigationGuardReturn {
  return checkPermission(
    PermissionObject.Transport,
    PermissionType.Update,
    'transport-list',
  )
}

export async function carrierAuctionGuard(to: RouteLocationNormalized): Promise<NavigationGuardReturn> {
  const contactHash = to.params.chash as string
  const auctionHash = to.params.hash as string

  const auctionsStore = useAuctionsStore()

  try {
    await fetchDataForCarrierByHash(contactHash)
    to.meta.auctionByHash = await auctionsStore.fetchCarrierAuction(
      auctionHash,
      contactHash,
    )

    return true
  }
  catch (e: unknown) {
    console.error('Failed to fetch carrier auction:', e)

    return isCarrierBlockedError(e) ? { name: 'carrier-blocked' } : { name: 'all' }
  }
}

function toastTenderRouteLoadFailure(toast: ReturnType<typeof useAppToast>, error: unknown): void {
  if (isAxiosError(error) && error.response?.status === HttpStatusCode.NotFound) {
    const detailRaw = isRecord(error.response.data) ? error.response.data.detail : undefined
    const detail = typeof detailRaw === 'string' ? detailRaw.trim() : ''

    toast.error(detail.length > 0 ? detail : i18n.global.t('Tender not found.'))

    return
  }

  toast.error(i18n.global.t('Failed to load tender. Please try again.'))
}

async function tenderCreateGuard(): Promise<NavigationGuardReturn> {
  return checkPermission(PermissionObject.Tender, PermissionType.Create, 'tenders-list')
}

async function tenderEditGuard(to: RouteLocationNormalized): Promise<NavigationGuardReturn> {
  const id = +to.params.id
  if (!Number.isFinite(id) || id <= 0) {
    return { name: 'tenders-list' }
  }

  const tendersStore = useTendersStore()
  const toast = useAppToast()

  try {
    const overview = await tendersStore.fetchOverview(id)
    const status = overview.tender.status

    if (!isTenderEditableDisplayStatus(status)) {
      toast.error(
        i18n.global.t('Tender cannot be edited because it is {status}.', {
          status: tenderDisplayStatusLabel(status, i18n.global.t),
        }),
      )

      return {
        name: 'contracts-tenders-view-id',
        params: { id: String(id) },
      }
    }

    const permType = getTenderModifyPermissionType(status)
    const permResult = checkPermission(PermissionObject.Tender, permType, 'tenders-list')
    if (permResult !== true) return permResult

    to.meta.tenderOverview = overview
    to.meta.tenderStatus = status

    return true
  }
  catch (error: unknown) {
    toastTenderRouteLoadFailure(toast, error)

    return { name: 'tenders-list' }
  }
}

/** Tender metadata is loaded by tenderViewGuard — price offers are fetched lazily by the tab composable. */
async function tenderViewGuard(to: RouteLocationNormalized): Promise<NavigationGuardReturn> {
  const id = +to.params.id
  if (!Number.isFinite(id) || id <= 0) {
    return { name: 'tenders-list' }
  }

  const tendersStore = useTendersStore()
  const toast = useAppToast()

  try {
    const { data } = await tendersStore.fetchTender(id)

    to.meta.tenderDetail = data

    return true
  }
  catch (error: unknown) {
    toastTenderRouteLoadFailure(toast, error)

    return { name: 'tenders-list' }
  }
}

async function contractViewGuard(to: RouteLocationNormalized): Promise<NavigationGuardReturn> {
  const id = +to.params.id
  const toast = useAppToast()

  if (!Number.isFinite(id) || id <= 0) {
    return { name: 'contracts-list' }
  }

  try {
    const { data } = await api.contracts.contractsRetrieve({ id })

    to.meta.contractDetail = data

    return true
  }
  catch (error: unknown) {
    if (isAxiosError(error) && error.response?.status === HttpStatusCode.NotFound) {
      toast.error(i18n.global.t('Contract not found.'))

      return { name: 'contracts-list' }
    }

    toast.error(i18n.global.t('Failed to load contract. Please try again.'))

    return { name: 'contracts-list' }
  }
}

export async function carrierTenderGuard(to: RouteLocationNormalized): Promise<NavigationGuardReturn> {
  const tenderHash = to.params.hash as string

  const carrierTendersStore = useCarrierTendersStore()

  try {
    const tender = (await carrierTendersStore.fetchTender(tenderHash)).data

    to.meta.tender = tender

    if (tender.contactHash) {
      await fetchDataForCarrierByHash(tender.contactHash)
    }

    return true
  }
  catch (e: unknown) {
    console.error('Failed to fetch carrier tender:', e)

    const toast = useAppToast()

    toast.error(i18n.global.t('Tender not found.'))

    return { name: 'carrier-request-list' }
  }
}

export async function carrierContractGuard(to: RouteLocationNormalized): Promise<NavigationGuardReturn> {
  const contractHash = to.params.hash as string
  const contractsStore = useCarrierContractsByEmailStore()

  try {
    const { data: contract } = await contractsStore.fetchCarrierContract(contractHash)

    to.meta.contract = contract

    return true
  }
  catch (e: unknown) {
    console.error('Failed to fetch carrier contract:', e)

    const toast = useAppToast()

    toast.error(i18n.global.t('Contract not found.'))

    return { name: 'carrier-request-list' }
  }
}

export async function carrierConsentsGuard(to: RouteLocationNormalized): Promise<NavigationGuardReturn> {
  const token = to.params.hash as string

  const privacyStore = usePrivacyStore()

  try {
    const consentsResponse = await privacyStore.fetchConsents(token, [
      ConsentTypesEnum.EMAIL,
    ])

    const termsResponse = await privacyStore.fetchTerms(token)

    to.meta.consents = consentsResponse.data
    to.meta.terms = termsResponse.data

    const contactHash = consentsResponse.data.contactHash || ''
    if (contactHash) await fetchDataForCarrierByHash(contactHash)

    return true
  }
  catch (e: unknown) {
    console.error('Failed to fetch privacy preferences:', e)

    return { name: 'all' }
  }
}

export async function carrierPortalGuard(to: RouteLocationNormalized): Promise<NavigationGuardReturn> {
  const otpHash = localStorage.getItem(CARRIER_PORTAL_TOKEN) || ''
  if (!otpHash) return { name: 'carrier-login', query: { to: to.path } }

  const otpStore = useCarrierPortalStore()
  try {
    await otpStore.fetchOneTimePassword(otpHash)

    return true
  }
  catch (e: unknown) {
    if (isCarrierBlockedError(e)) return { name: 'carrier-blocked' }

    // Already in the app with expired OTP — stay on current page, session expiry dialog handles it.
    // On fresh page load (store empty), fall through to redirect to login.
    if (isAxiosError(e) && e.response?.data?.code?.includes('otp_expired') && otpStore.getOtpHash) {
      return false
    }

    console.error('Failed to fetch carrier one time password:', e)

    return { name: 'carrier-login', query: { to: to.path } }
  }
}

export async function carrierBlockedPageGuard(): Promise<NavigationGuardReturn> {
  const otpHash = localStorage.getItem(CARRIER_PORTAL_TOKEN) || ''

  // Blocked carriers arriving from a by-hash link have no portal session to verify against.
  if (!otpHash) return true

  const otpStore = useCarrierPortalStore()
  try {
    await otpStore.fetchOneTimePassword(otpHash)

    return {
      name: 'carrier-request-list',
      query: { carrierAuctionStatus: CarrierAuctionStatusEnum.ACTIVE },
    }
  }
  catch {
    return true // can't prove they're allowed in — show the blocked page
  }
}

export async function loggedCarrierRedirectGuard(): Promise<NavigationGuardReturn> {
  const otpHash = localStorage.getItem(CARRIER_PORTAL_TOKEN) || ''

  if (!otpHash) {
    return true
  }

  const otpStore = useCarrierPortalStore()
  try {
    await otpStore.fetchOneTimePassword(otpHash)

    return {
      name: 'carrier-request-list',
      query: { carrierAuctionStatus: CarrierAuctionStatusEnum.ACTIVE },
    }
  }
  catch (e: unknown) {
    return isCarrierBlockedError(e) ? { name: 'carrier-blocked' } : true
  }
}

export async function loggedUserRedirectGuard(): Promise<NavigationGuardReturn> {
  const authStore = useAuthStore()

  if (isUserLoggedIn()) {
    try {
      await authStore.fetchCurrentUser()

      return { name: 'request-list' }
    }
    catch (e: unknown) {
      if (isAxiosError(e)) return await checkGuardDataError(e, 'request-list')

      return { name: 'error' }
    }
  }

  return true
}

export async function switchWorkspaceGuard(to: RouteLocationNormalized): Promise<NavigationGuardReturn> {
  const workspaceId = to.params['workspaceId']
  if (!workspaceId) {
    return true
  }

  const toast = useAppToast()

  try {
    const response = await switchWorkspaceHelper(+workspaceId)

    toast.info(
      i18n.global.t('Active workspace changed to {name}', {
        name: response.data.workspace.name,
      }),
    )
  }
  catch {
    toast.error(
      i18n.global.t('Workspace does not exist or you do not have access to it'),
    )
  }

  return to.fullPath.replace(`/w/${workspaceId}`, '')
}

async function checkGuardDataError(
  error: AxiosError,
  redirectName?: string,
  unauthorizedRedirectName?: string,
): Promise<NavigationGuardReturn> {
  if (error.response?.status === 401) {
    try {
      await refreshTokenHelper()
      await fetchData()

      if (redirectName) return { name: redirectName }

      return true
    }
    catch {
      // if there is error during token refresh we should redirect to login or register page based on user navigation
      clearAuthData()
      if (unauthorizedRedirectName) return { name: unauthorizedRedirectName }

      return true
    }
  }

  return { name: 'error' }
}

async function fetchData() {
  const authStore = useAuthStore()
  if (!authStore.currentUser) await authStore.fetchCurrentUser()

  if (authStore.currentUser)
    Sentry.setUser({
      id: authStore.currentUser.id,
      email: authStore.currentUser.email,
    })
}

async function fetchDataForCarrierByHash(hash: string) {
  const carrierStore = useCarriersStore()
  if (carrierStore.currentCarrierByHash?.hash !== hash)
    await carrierStore.fetchCurrentCarrierByHash(hash)

  Sentry.setUser({ id: hash })
}

function checkPermission(
  object: PermissionObject,
  type: PermissionType,
  redirect: string,
): NavigationGuardReturn {
  const authStore = useAuthStore()
  const toast = useAppToast()

  if (
    !authStore.currentUser
    || !userHasPermissions(authStore.currentUser, object, type)
  ) {
    toast.error(
      i18n.global.t('You do not have permission to access this object'),
    )

    return { name: redirect }
  }

  return true
}
